import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/features/auth/hooks/use-auth";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import type { UserRole } from "@/types/api";

interface ProtectedRouteProps {
  /** If provided, the user's role must be one of these to access the route. */
  allowedRoles?: readonly UserRole[];
  /** When true, allow access even if requiresPasswordChange is true. */
  allowDuringPasswordChange?: boolean;
}

/**
 * Gate that redirects unauthenticated users to /login and (optionally)
 * authorizes by role. When the user's `requiresPasswordChange` flag is true,
 * redirect to /change-password unless the route opts out.
 */
export function ProtectedRoute({
  allowedRoles,
  allowDuringPasswordChange = false,
}: ProtectedRouteProps) {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-page flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (user.requiresPasswordChange && !allowDuringPasswordChange) {
    return <Navigate to="/change-password" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/schedules" replace />;
  }

  return <Outlet />;
}
